import { useEffect, useMemo, useRef } from 'react';

import {
  type RenderMetrics,
  buildRenderEventProperties,
} from '@modules/_shared/components/report-editor/reportEditorPerformance';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  ANALYTICS_EVENTS,
  getBaseCmaEventProperties,
  usePostHogClient,
} from '@shared/analytics';

export type ReportEditorPerformanceTracker = {
  startEditTracking: () => void;
  cancelEditTracking: () => void;
  onDataFetchComplete: () => void;
  onTemplateRenderComplete: () => void;
  onIframeRenderComplete: () => void;
  onIframeSwapComplete: () => void;
};

type UseReportEditorPerformanceOptions = {
  documentType: string;
  documentId: string;
};

export const useReportEditorPerformance = ({
  documentType,
  documentId,
}: UseReportEditorPerformanceOptions): ReportEditorPerformanceTracker => {
  const { track } = usePostHogClient();
  const { me } = useAppData();
  const metricsRef = useRef<RenderMetrics | null>(null);
  const latestRef = useRef({ track, me, documentType, documentId });

  useEffect(() => {
    latestRef.current = { track, me, documentType, documentId };
  }, [track, me, documentType, documentId]);

  return useMemo(() => {
    const mark =
      (
        phase: 'dataFetchedAt' | 'templateRenderedAt' | 'iframeRenderedAt',
        previousPhase: keyof RenderMetrics,
      ) =>
      () => {
        const metrics = metricsRef.current;
        if (metrics && metrics[previousPhase] != null) {
          metrics[phase] = performance.now();
        }
      };

    return {
      startEditTracking: () => {
        metricsRef.current = {
          startedAt: performance.now(),
          dataFetchedAt: null,
          templateRenderedAt: null,
          iframeRenderedAt: null,
        };
      },
      cancelEditTracking: () => {
        metricsRef.current = null;
      },
      onDataFetchComplete: mark('dataFetchedAt', 'startedAt'),
      onTemplateRenderComplete: mark('templateRenderedAt', 'dataFetchedAt'),
      onIframeRenderComplete: mark('iframeRenderedAt', 'templateRenderedAt'),
      onIframeSwapComplete: () => {
        const metrics = metricsRef.current;
        if (!metrics || metrics.iframeRenderedAt == null) {
          return;
        }
        metricsRef.current = null;

        const latest = latestRef.current;
        latest.track(ANALYTICS_EVENTS.REPORT_EDITOR_RENDER_COMPLETED, {
          ...getBaseCmaEventProperties(latest.me, latest.documentId),
          document_type: latest.documentType,
          document_id: latest.documentId,
          ...buildRenderEventProperties(metrics, performance.now()),
        });
      },
    };
  }, []);
};
